import { Snowflake, Trash2 } from 'lucide-react';

import { UnfreezeIcon } from '@/components/icons/Unfreeze.icon';
import { StatusOptionBuilderParams } from '@/sections/asset/types';
import { AssetStatus } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';

/**
 * Builds status management dropdown options
 * Conditionally shows freeze/unfreeze based on current asset states and user permissions
 * Includes keyboard shortcut hints
 *
 * @param params - Parameters for building status options
 * @returns Array of dropdown options for status management actions
 */
export const getAssetStatusOptions = ({
  assets,
  handlers,
}: StatusOptionBuilderParams): DropdownOption[] => {
  const allFrozen = assets.every(asset => asset.status === AssetStatus.Frozen);
  const allActive = assets.every(asset => asset.status === AssetStatus.Active);
  const showBoth = !allFrozen && !allActive;

  return [
    // Show Freeze option if all active or mixed states
    ...(allActive || showBoth
      ? [
          {
            label: 'Freeze Asset',
            icon: <Snowflake className="size-4" />,
            onClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Frozen),
            count: ` ⌥ F`,
          },
        ]
      : []),
    // Show Unfreeze option if all frozen or mixed states
    ...(allFrozen || showBoth
      ? [
          {
            label: 'Unfreeze Asset',
            icon: <UnfreezeIcon className="size-4" />,
            onClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Active),
            count: ` ⌥ U`,
          },
        ]
      : []),
    // Always show Delete option
    {
      label: 'Delete Asset',
      icon: <Trash2 className="size-4" />,
      onClick: () => handlers.handleUpdateStatus(assets, AssetStatus.Deleted),
      count: ` ⌥ D`,
    },
  ];
};
